import { BookListingEstimate, BookListingQuote } from "~/types/api-types";
import type { FsvDepositPriceBreakdown } from "~/types/fsv.types";

/**
 * Converts API gratuity percent (whole number or 0–1 fraction) for
 * `FormattedNumber` with `style="percent"` (e.g. `20` or `0.2` → `0.2`).
 */
const normalizeGratuityRateForPercentStyle = (
  rate: number | undefined,
): number => {
  if (rate == null || !Number.isFinite(rate) || rate <= 0) return 0;
  return rate > 1 ? rate / 100 : rate;
};

/**
 * Line-item figures for FSV minimum-spend deposit listings (quote or estimate).
 * `totalBeforeTaxes` (`platform_and_venue_total`) and `dueAtVenue`
 * (`due_at_venue_amount`, already net of the deposit) come from the API, not
 * client-side sums.
 */
const getFsvDepositPriceBreakdown = ({
  quote,
  estimate,
  useEstimate,
}: {
  quote?: BookListingQuote | null;
  estimate?: BookListingEstimate | null;
  useEstimate: boolean;
}): FsvDepositPriceBreakdown | null => {
  if (useEstimate && estimate) {
    const spaceRental = estimate.flat_rate_booking_subtotal ?? 0;
    const minSpend = estimate.flat_rate_fandb_amount ?? 0;
    const processing = estimate.guest_processing_fee ?? 0;
    return {
      spaceRental,
      minSpend,
      processing,
      totalBeforeTaxes: estimate.total ?? 0,
      dueToday: estimate.deposit_amount ?? 0,
      dueAtVenue: estimate.due_at_venue_amount ?? 0,
    };
  }

  const calculations = quote?.masterContract?.calculations;
  if (!calculations) return null;

  const guest = calculations.guest;
  const payAtVenue = guest?.pay_at_venue;
  const spaceRental = calculations.space_rental_guest ?? 0;
  const minSpend = calculations?.included_items_guest ?? 0;
  const processing = guest?.fee ?? 0;
  const dueToday = payAtVenue?.deposit_amount ?? 0;

  return {
    spaceRental,
    minSpend,
    processing,
    totalBeforeTaxes: payAtVenue?.platform_and_venue_total ?? 0,
    dueToday,
    dueAtVenue: payAtVenue?.due_at_venue_amount ?? 0,
  };
};

export { normalizeGratuityRateForPercentStyle, getFsvDepositPriceBreakdown };
